import React, { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldPathByValue,
  FieldPathValue,
  FieldValues,
  UseFormClearErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import get from 'lodash/get';
import { ConsentSelect } from '@components/consents/ConsentSelect/ConsentSelect';
import { Spinner } from '@components/spinner/Spinner';
import { Typography } from '@naf-simba/building-blocks-typography';
import { useConsentsApi } from '@hooks/Consents/useConsentsApi';
import { UserInteraction } from '@naf/user-interaction';
import { Checkbox } from '@naf-simba/elements-checkbox';
import ConsentContent from '@components/consents/ConsentContent/ConsentContent';
import { MainMemberConsent } from '../../../../../../../types/membersFormType';
import * as S from './ConsentsForm.styles';

type ConsentPath<TFormValues extends FieldValues> = FieldPathByValue<TFormValues, MainMemberConsent[]>;

interface ConsentsProps<TFormValues extends FieldValues> {
  errors: FieldErrors<TFormValues>;
  setValue: UseFormSetValue<TFormValues>;
  control: Control<TFormValues>;
  clearErrors: UseFormClearErrors<TFormValues>;
  consentPath?: ConsentPath<TFormValues>;
  initialConsents?: MainMemberConsent[];
}

export const ConsentsForms = <TFormValues extends FieldValues>({
  setValue,
  control,
  clearErrors,
  errors,
  consentPath = 'mainMember.consents' as ConsentPath<TFormValues>,
  initialConsents,
}: ConsentsProps<TFormValues>) => {
  const { consents, refetch, isLoading, isError } = useConsentsApi(true);
  const watchedConsentsList = useWatch({ control, name: consentPath }) as MainMemberConsent[];
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [showMoreStates, setShowMoreStates] = useState<Record<number, boolean>>({});

  const consentFieldPath = (index: number) => `${consentPath}.${index}` as FieldPath<TFormValues>;

  const handleSelectAll = () => {
    const selectedAll = !isSelectedAll;
    setIsSelectedAll(selectedAll);

    if (selectedAll) {
      watchedConsentsList?.forEach((consent) => {
        clearErrors([consentFieldPath(consent.indexId)]);
        setConsentValue({ ...consent, consented: true });
      });
    }

    if (!selectedAll) {
      watchedConsentsList?.forEach((consent) => {
        clearErrors([consentFieldPath(consent.indexId)]);
        setConsentValue({ ...consent, consented: false });
      });
    }
  };

  const handleSelect = (consent: MainMemberConsent, consented: boolean) => {
    setConsentValue({ ...consent, consented });
  };

  const setConsentValue = (consent: MainMemberConsent) => {
    const fieldPath = consentFieldPath(consent.indexId);

    setValue(fieldPath, {
      id: consent.id,
      indexId: consent.indexId,
      consented: consent.consented,
      undetermined: false,
    } as FieldPathValue<TFormValues, typeof fieldPath>);

    clearErrors([fieldPath]);
  };

  const toggleShowMore = (consentNumber: string) => {
    setShowMoreStates((prev) => ({
      ...prev,
      [consentNumber]: !prev[consentNumber],
    }));
  };

  useEffect(() => {
    if (consents?.requiredConsents?.consents) {
      const defaultConsents = consents.requiredConsents.consents.map((consent, index) => {
        const initialConsent = initialConsents?.find(
          ({ id }) => id === consent.statement.consentNumber || (!id && !consent.statement.consentNumber),
        );

        return {
          id: consent.statement.consentNumber || null,
          indexId: index,
          consented: initialConsent?.consented ?? false,
          undetermined: initialConsent?.undetermined ?? true,
        };
      });

      setValue(consentPath, defaultConsents as FieldPathValue<TFormValues, typeof consentPath>);
    }
  }, [consentPath, consents, initialConsents, setValue]);

  useEffect(() => {
    if (watchedConsentsList) {
      const allSelected =
        watchedConsentsList.length > 0 && watchedConsentsList.every((consent) => consent.consented === true);
      setIsSelectedAll(allSelected);
    }
  }, [watchedConsentsList]);

  if (isLoading) {
    return <Spinner />;
  }

  if (isError || !consents) {
    return (
      <S.UserInteractionWrapper>
        <UserInteraction variant="error" button={{ children: 'Prøv igjen', onClick: () => refetch() }}>
          Oops
        </UserInteraction>
      </S.UserInteractionWrapper>
    );
  }

  return (
    <>
      <Typography variant="paragraphM">{consents?.legitimateInterest?.statement}</Typography>
      <S.SelectAllToggleWrapper>
        <Checkbox
          name="selectAll"
          checked={isSelectedAll}
          onChange={handleSelectAll}
          label="Jeg samtykker “Ja” til alle punktene under"
        />
      </S.SelectAllToggleWrapper>
      <S.AdditionalStatementWrapper>
        <Typography variant="titleM">Du må ta stilling til alle samtykkene for å kunne gå videre.</Typography>
      </S.AdditionalStatementWrapper>
      {consents?.requiredConsents.consents.map((consent, index) => (
        <ConsentContent
          key={consent.statement.consentNumber}
          consent={consent}
          onReadMoreToggle={() => toggleShowMore(consent.statement.consentNumber)}
          isReadMoreActive={showMoreStates[consent.statement.consentNumber] && !!consent.statement.helpText}
          consentSelectItem={
            <Controller<TFormValues>
              name={consentFieldPath(index)}
              control={control}
              defaultValue={
                {
                  id: consent.statement.consentNumber,
                  indexId: index,
                  consented: false,
                  undetermined: true,
                } as FieldPathValue<TFormValues, FieldPath<TFormValues>>
              }
              render={({ field: { value } }) => {
                const consentValue = value as MainMemberConsent;

                return (
                  <ConsentSelect
                    errorMessage={
                      (get(errors, `${consentPath}.${index}.message`) as string | undefined) ||
                      (get(errors, `${consentPath}.${index}.undetermined.message`) as string | undefined)
                    }
                    onSelectYes={() => handleSelect(consentValue, true)}
                    onSelectNo={() => handleSelect(consentValue, false)}
                    consented={consentValue.consented || false}
                    undetermined={consentValue.undetermined || false}
                  />
                );
              }}
            />
          }
        />
      ))}
    </>
  );
};
